import { useMemo, useState } from 'react';
import { Receipt, RefreshCw } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import type { Passbook } from '@/shared/types';
import { useParticipantHistory } from '../../api/use-participant';
import { buildHistory, groupByMonth, type HistoryEntry } from '../../model/history';
import { HistoryEntryRow } from '../history-entry-row';

const PAGE = 20;

interface HistoryTabProps {
  idPassbook: string;
  passbook: Passbook;
}

/** Movimentações do cartão, agrupadas por mês — mesmas regras de rótulo do extrato do admin. */
export function HistoryTab({ idPassbook, passbook }: HistoryTabProps) {
  const { data, isPending, isError, refetch, isRefetching } = useParticipantHistory(idPassbook);
  const [visible, setVisible] = useState(PAGE);
  const entries = useMemo<HistoryEntry[]>(() => (data ? buildHistory(data, passbook) : []), [data, passbook]);
  const months = useMemo(() => groupByMonth(entries.slice(0, visible)), [entries, visible]);

  if (isPending) {
    return (
      <Card className="mx-auto w-full max-w-xl space-y-4 p-5">
        <Skeleton className="h-4 w-32" />
        {Array.from({ length: 5 }, (_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="size-9 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3 w-1/2" />
              <Skeleton className="h-3 w-1/3" />
            </div>
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </Card>
    );
  }

  if (isError && !data) {
    return (
      <Card className="mx-auto flex w-full max-w-xl flex-col items-center gap-3 p-8 text-center">
        <p className="font-medium">Não foi possível carregar seu histórico.</p>
        <Button variant="outline" onClick={() => void refetch()} loading={isRefetching}>
          <RefreshCw className="size-4" />
          Tentar de novo
        </Button>
      </Card>
    );
  }

  if (entries.length === 0) {
    return (
      <Card className="mx-auto flex w-full max-w-xl flex-col items-center gap-2 p-8 text-center">
        <span className="grid size-12 place-items-center rounded-2xl bg-muted">
          <Receipt className="size-6 text-muted-foreground" />
        </span>
        <p className="font-medium">Suas compras aparecem aqui</p>
        <p className="max-w-xs text-sm text-muted-foreground">
          Informe seu CPF no caixa a cada compra para acumular e acompanhar por aqui.
        </p>
      </Card>
    );
  }

  return (
    <Card className="mx-auto w-full max-w-xl p-5">
      {months.map((month) => (
        <section key={month.key} className="mb-2 last:mb-0">
          <h3 className="pt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{month.label}</h3>
          <ul className="divide-y divide-border/60">
            {month.entries.map((entry) => (
              <HistoryEntryRow key={entry.id} entry={entry} />
            ))}
          </ul>
        </section>
      ))}
      {visible < entries.length ? (
        <Button variant="outline" className="mt-4 w-full" onClick={() => setVisible((v) => v + PAGE)}>
          Ver mais
        </Button>
      ) : null}
    </Card>
  );
}
